import React, { startTransition } from 'react';
import { Helmet } from 'react-helmet';
import { FacebookIcon, TelegramIcon, WhatsappIcon } from 'react-share';
import { useNavigate } from 'react-router-dom';

const CommunityPage: React.FC = () => {
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    startTransition(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      navigate(path);
    });
  };

  return (
    <>
      <Helmet>
        <title>Join Our Community | RentSmart.ie</title>
        <meta
          name="description"
          content="Join the RentSmart.ie community. Connect with renters, landlords, and property enthusiasts across Ireland. Share experiences, get advice, and stay updated."
        />
        <meta
          name="keywords"
          content="RentSmart community, rental property community Ireland, landlord network, renter community, property discussion, Facebook group, Telegram, WhatsApp"
        />
        <link rel="canonical" href="https://rentsmart.ie/community" />
        <meta property="og:title" content="Join Our Community | RentSmart.ie" />
        <meta
          property="og:description"
          content="Connect with thousands of renters and landlords across Ireland. Share experiences, ask questions, and stay informed."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rentsmart.ie/community" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Join Our Community
            </h1>
            <p className="text-xl text-gray-600 mb-6">
              Connect with thousands of renters, landlords, and property enthusiasts across Ireland.
              Share experiences, ask questions, and stay informed about the rental market.
            </p>
          </div>

          {/* Community Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">💬</div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Connect</h2>
              <p className="text-gray-600">
                Meet other members of the RentSmart community and share your experiences
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">💡</div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Learn</h2>
              <p className="text-gray-600">
                Get advice and tips from experienced landlords, renters, and property professionals
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-lg transition-shadow">
              <div className="text-4xl mb-4">📢</div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Stay Updated</h2>
              <p className="text-gray-600">
                Get the latest news, updates, and announcements about the rental market
              </p>
            </div>
          </div>

          {/* Social Media Links Section */}
          <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Follow Us On Social Media
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Facebook */}
              <a
                href="https://www.facebook.com/groups/1295007891494794"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-8 text-center hover:shadow-lg transition-all transform hover:-translate-y-1"
              >
                <div className="flex justify-center mb-4">
                  <FacebookIcon size={64} round className="h-16 w-16 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Facebook</h3>
                <p className="text-gray-600 mb-4 no-underline">
                  Join our Facebook community group to discuss rental properties, ask questions, and
                  connect with other members
                </p>
                <span className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors">
                  Join Group
                </span>
              </a>

              {/* Telegram */}
              <a
                href="https://t.me/rentsmart_ie"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-8 text-center hover:shadow-lg transition-all transform hover:-translate-y-1"
              >
                <div className="flex justify-center mb-4">
                  <TelegramIcon size={64} round className="h-16 w-16 text-blue-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Telegram</h3>
                <p className="text-gray-600 mb-4 no-underline">
                  Subscribe to our Telegram channel for real-time updates, market insights, and
                  exclusive announcements
                </p>
                <span className="inline-block bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors">
                  Subscribe
                </span>
              </a>

              {/* WhatsApp */}
              <a
                href="https://chat.whatsapp.com/JQXsWjrrybP8covAaVggnD"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-8 text-center hover:shadow-lg transition-all transform hover:-translate-y-1"
              >
                <div className="flex justify-center mb-4">
                  <WhatsappIcon size={64} round className="h-16 w-16 text-green-500" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">WhatsApp</h3>
                <p className="text-gray-600 mb-4 no-underline">
                  Join our WhatsApp group for instant messaging, quick tips, and direct support from
                  our community team
                </p>
                <span className="inline-block bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors">
                  Join Chat
                </span>
              </a>
            </div>
          </div>

          {/* Community Guidelines */}
          <div className="mt-12 bg-white rounded-lg shadow-lg p-8 md:p-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Community Guidelines</h2>
            <div className="space-y-4 text-gray-700">
              <p>
                Our community is built on respect, honesty, and a shared commitment to helping each
                other navigate the rental market. Please follow these guidelines:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start">
                  <span className="text-green-500 font-bold mr-3">✓</span>
                  <span>Be respectful and courteous to all community members</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 font-bold mr-3">✓</span>
                  <span>Share helpful information and experiences</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 font-bold mr-3">✓</span>
                  <span>Keep discussions relevant and on-topic</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 font-bold mr-3">✓</span>
                  <span>No spam, harassment, or discriminatory content</span>
                </li>
                <li className="flex items-start">
                  <span className="text-green-500 font-bold mr-3">✓</span>
                  <span>Report inappropriate content to moderators</span>
                </li>
              </ul>
              <div className="mt-6 p-4 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                <p className="text-sm">
                  For more information, please review our full{' '}
                  <button
                    onClick={() => handleNavigate('/community-guidelines')}
                    className="text-blue-600 hover:text-blue-800 font-semibold underline"
                  >
                    Community Guidelines
                  </button>
                  .
                </p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-12 text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Ready to Join?</h2>
            <p className="text-gray-600 mb-8">
              Pick your preferred platform and start connecting with the RentSmart community today!
            </p>
            <button
              onClick={() => handleNavigate('/browse')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
            >
              Browse Properties
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommunityPage;
